import { AccessTime as ClockIcon } from '@mui/icons-material';
import { Box, Popover, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import Picker, { PickerValue } from 'react-mobile-picker';

import { Icon } from '@/components/common/Icon';

const FONT = "'Manrope', sans-serif";

/* ─── Calendar helpers ─────────────────────────────────────── */

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface CalCell {
  day: number;
  month: number;
  year: number;
  current: boolean;
}

const buildCalCells = (year: number, month: number): CalCell[] => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: CalCell[] = [];

  if (firstDay > 0) {
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    const dip = new Date(py, pm + 1, 0).getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({ day: dip - i, month: pm, year: py, current: false });
    }
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, current: true });
  }
  const nm = month === 11 ? 0 : month + 1;
  const ny = month === 11 ? year + 1 : year;
  let nd = 1;
  while (cells.length < 42) {
    cells.push({ day: nd++, month: nm, year: ny, current: false });
  }
  return cells;
};

/* ─── Time drum helpers ────────────────────────────────────── */

const HOURS = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

const dateToPickerVal = (d: Date | null): PickerValue => {
  if (!d || isNaN(d.getTime())) return { hour: '12', minute: '00', ampm: 'AM' };
  const h = d.getHours();
  const rounded = Math.round(d.getMinutes() / 5) * 5;
  return {
    hour: (h % 12 || 12).toString().padStart(2, '0'),
    minute: (rounded % 60).toString().padStart(2, '0'),
    ampm: h < 12 ? 'AM' : 'PM',
  };
};

const pickerValToDate = (pv: PickerValue): Date => {
  const h = parseInt(pv.hour as string);
  const m = parseInt(pv.minute as string);
  const isPm = pv.ampm === 'PM';
  const h24 = isPm ? (h === 12 ? 12 : h + 12) : h === 12 ? 0 : h;
  const d = new Date();
  d.setHours(h24, m, 0, 0);
  return d;
};

const drumItemStyle = (selected: boolean): React.CSSProperties => ({
  width: '100%',
  textAlign: 'center',
  fontSize: 14,
  fontWeight: selected ? 600 : 400,
  color: selected ? 'var(--tx-muted)' : 'var(--tx-faint)',
  fontFamily: FONT,
  lineHeight: '28px',
});

/* ─── Component ────────────────────────────────────────────── */

export interface DateTimePickerButtonProps {
  date: Date | null;
  time: Date | null;
  onConfirm: (date: Date | null, time: Date | null) => void;
  placeholder?: string;
}

export const DateTimePickerButton = ({
  date,
  time,
  onConfirm,
  placeholder = 'Set Date / Time',
}: DateTimePickerButtonProps) => {
  const now = new Date();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [viewYear, setViewYear] = useState(date?.getFullYear() ?? now.getFullYear());
  const [viewMonth, setViewMonth] = useState(date?.getMonth() ?? now.getMonth());
  const [tempDate, setTempDate] = useState<Date | null>(date);
  const [showTime, setShowTime] = useState(!!time);
  const [pickerVal, setPickerVal] = useState<PickerValue>(dateToPickerVal(time));
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setTempDate(date);
    setShowTime(!!time);
    setPickerVal(dateToPickerVal(time));
    setViewYear(date?.getFullYear() ?? now.getFullYear());
    setViewMonth(date?.getMonth() ?? now.getMonth());
    setAnchorEl(e.currentTarget);
  };

  const handleDone = () => {
    onConfirm(tempDate, showTime ? pickerValToDate(pickerVal) : null);
    setAnchorEl(null);
  };

  const handleCancel = () => setAnchorEl(null);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const calCells = useMemo(() => buildCalCells(viewYear, viewMonth), [viewYear, viewMonth]);

  const isSel = (c: CalCell) =>
    !!tempDate &&
    c.day === tempDate.getDate() &&
    c.month === tempDate.getMonth() &&
    c.year === tempDate.getFullYear();

  const isTdy = (c: CalCell) =>
    c.day === now.getDate() && c.month === now.getMonth() && c.year === now.getFullYear();

  const displayLabel = (() => {
    if (!date) return null;
    const dp = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (!time) return dp;
    const tp = time.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${dp} · ${tp}`;
  })();

  const timeLabel = showTime
    ? `${pickerVal.hour}:${pickerVal.minute} ${pickerVal.ampm}`
    : 'Set time';

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        sx={{
          // the standard 40px control (ds), a calendar glyph before the value
          width: '100%',
          height: '40px',
          px: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'var(--tx-fill)',
          border: 0,
          borderRadius: '12px',
          color: displayLabel ? 'var(--tx-text)' : 'var(--tx-faint)',
          fontFamily: FONT,
          fontSize: 13,
          cursor: 'pointer',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          '&:hover': { background: 'var(--tx-fill-2)' },
        }}>
        <Box component="span" sx={{ display: 'flex', color: 'var(--tx-faint)', flexShrink: 0 }}>
          <Icon name="calendar" size={15} />
        </Box>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {displayLabel ?? placeholder}
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 400,
        }}
        slotProps={{
          root: {
            sx: {
              zIndex: (theme) => theme.zIndex.modal + 400,
            },
          },
          paper: {
            sx: {
              mt: '6px',
              zIndex: (theme) => theme.zIndex.modal + 401,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              borderRadius: '16px',
              boxShadow: 'var(--tx-shadow)',
              overflow: 'hidden',
              width: 288,
            },
          },
        }}>
        {/* Calendar */}
        <Box sx={{ p: '20px 20px 0' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: '10px',
            }}>
            <Box
              component="button"
              type="button"
              onClick={prevMonth}
              sx={{
                background: 'none',
                border: 'none',
                color: 'var(--tx-faint)',
                cursor: 'pointer',
                fontSize: 22,
                lineHeight: 1,
                px: '6px',
                py: '2px',
                borderRadius: '6px',
                fontFamily: FONT,
                '&:hover': { color: 'var(--tx-text)', background: 'rgba(255,255,255,0.07)' },
              }}>
              ‹
            </Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 15, color: 'var(--tx-text)' }}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={nextMonth}
              sx={{
                background: 'none',
                border: 'none',
                color: 'var(--tx-faint)',
                cursor: 'pointer',
                fontSize: 22,
                lineHeight: 1,
                px: '6px',
                py: '2px',
                borderRadius: '6px',
                fontFamily: FONT,
                '&:hover': { color: 'var(--tx-text)', background: 'rgba(255,255,255,0.07)' },
              }}>
              ›
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: '2px' }}>
            {DAY_HEADERS.map((d, i) => (
              <Box
                key={i}
                sx={{
                  textAlign: 'center',
                  fontSize: 11,
                  fontFamily: FONT,
                  color: 'var(--tx-faint)',
                  py: '3px',
                }}>
                {d}
              </Box>
            ))}
          </Box>

          <Box
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', mb: '12px' }}>
            {calCells.map((cell, i) => {
              const sel = isSel(cell);
              const tdy = isTdy(cell);
              return (
                <Box
                  key={i}
                  component="button"
                  type="button"
                  onClick={() => setTempDate(new Date(cell.year, cell.month, cell.day))}
                  sx={{
                    background: sel ? 'var(--tx-fill-2)' : 'transparent',
                    border:
                      tdy && !sel ? '1px solid rgba(255,255,255,0.28)' : '1px solid transparent',
                    borderRadius: '8px',
                    color: sel ? 'var(--tx-text)' : cell.current ? 'var(--tx-text)' : 'var(--tx-faint)',
                    fontFamily: FONT,
                    fontSize: 12,
                    fontWeight: sel ? 600 : 400,
                    cursor: 'pointer',
                    py: '9px',
                    textAlign: 'center',
                    lineHeight: 1,
                    '&:hover': { background: sel ? '#C8C4BD' : 'rgba(255,255,255,0.08)' },
                  }}>
                  {cell.day}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Set time */}
        <Box sx={{ pl: '28px', pr: '20px', pt: '14px', pb: showTime ? '4px' : '16px' }}>
          <Box
            component="button"
            type="button"
            onClick={() => setShowTime((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              background: 'none',
              border: 'none',
              color: showTime ? 'var(--tx-muted)' : 'var(--tx-faint)',
              fontFamily: FONT,
              fontSize: 14,
              cursor: 'pointer',
              p: 0,
              textAlign: 'left',
              '&:hover': { color: 'var(--tx-text)' },
            }}>
            <ClockIcon sx={{ fontSize: 18 }} />
            {timeLabel}
          </Box>

          {showTime && (
            <Box
              sx={{
                mt: '8px',
                position: 'relative',
                '& > div:last-child > div:last-child': { display: 'none' },
              }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  height: 28,
                  mt: '-14px',
                  background: 'var(--tx-fill)',
                  borderRadius: '8px',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
              <Picker
                value={pickerVal}
                onChange={setPickerVal}
                height={112}
                itemHeight={28}
                wheelMode="normal"
                style={{ background: 'transparent' }}>
                <Picker.Column name="hour">
                  {HOURS.map((h) => (
                    <Picker.Item key={h} value={h}>
                      {({ selected }) => <div style={drumItemStyle(selected)}>{h}</div>}
                    </Picker.Item>
                  ))}
                </Picker.Column>
                <Picker.Column name="minute">
                  {MINUTES.map((m) => (
                    <Picker.Item key={m} value={m}>
                      {({ selected }) => <div style={drumItemStyle(selected)}>{m}</div>}
                    </Picker.Item>
                  ))}
                </Picker.Column>
                <Picker.Column name="ampm">
                  {['AM', 'PM'].map((ap) => (
                    <Picker.Item key={ap} value={ap}>
                      {({ selected }) => <div style={drumItemStyle(selected)}>{ap}</div>}
                    </Picker.Item>
                  ))}
                </Picker.Column>
              </Picker>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', p: '14px 20px' }}>
          <Box
            component="button"
            type="button"
            onClick={handleCancel}
            sx={{
              height: 36,
              padding: '0 20px',
              background: 'var(--tx-fill)',
              color: 'var(--tx-muted)',
              border: 0,
              borderRadius: '10px',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { color: 'var(--tx-text)', background: 'var(--tx-fill-2)' },
            }}>
            Cancel
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleDone}
            sx={{
              height: 36,
              padding: '0 20px',
              background: 'var(--tx-fill-2)',
              color: 'var(--tx-text)',
              border: 'none',
              borderRadius: '10px',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
            Done
          </Box>
        </Box>
      </Popover>
    </>
  );
};
